import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { Toast } from 'radix-ui'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'
import { dismissToast, useToasts, type ToastTone } from './toast-store'

const toneStyles: Record<ToastTone, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: 'text-success-600' },
  error: { icon: XCircle, className: 'text-danger-600' },
  info: { icon: Info, className: 'text-info-600' },
  warning: { icon: AlertTriangle, className: 'text-warning-600' },
}

/** Renders queued toasts in an ARIA live region (Radix Toast handles swipe, pause-on-hover, F8 focus). */
export function Toaster() {
  const items = useToasts()
  return (
    <Toast.Provider swipeDirection="right" duration={5000}>
      {items.map((t) => {
        const { icon: Icon, className } = toneStyles[t.tone]
        return (
          <Toast.Root
            key={t.id}
            type={t.tone === 'error' ? 'foreground' : 'background'}
            onOpenChange={(open) => (open ? undefined : dismissToast(t.id))}
            className="pointer-events-auto flex w-full animate-slide-up items-start gap-3 rounded-card border border-line bg-surface p-4 shadow-overlay data-[swipe=end]:animate-fade-in data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]"
          >
            <Icon className={cn('mt-0.5 size-5 shrink-0', className)} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <Toast.Title className="text-sm font-semibold text-ink">{tAny(t.title)}</Toast.Title>
              {t.description ? (
                <Toast.Description className="mt-0.5 text-sm text-muted">
                  {tAny(t.description)}
                </Toast.Description>
              ) : null}
            </div>
            <Toast.Close
              aria-label={tAny('Dismiss notification')}
              className="grid size-6 place-items-center rounded-xs text-subtle hover:bg-surface-sunken hover:text-ink"
            >
              <X className="size-4" />
            </Toast.Close>
          </Toast.Root>
        )
      })}
      <Toast.Viewport className="pointer-events-none fixed right-0 bottom-0 z-[100] flex w-full max-w-sm flex-col gap-2 p-4 outline-none sm:right-2 sm:bottom-2" />
    </Toast.Provider>
  )
}
